import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Grid, Backdrop, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {Black, Blue} from 'wobi-web-common/dist/config/colors.config';
import Loader from 'wobi-web-common/dist/components/Loader';
import {handleContactUsSubmit} from '../../utils/apiHandlers';
import OffersContainer from '../offers/OffersContainer';
import NoOffers from '../offers/NoOffers';
import ErrorMessagePopup from '../../components/modals/ErrorMessage';
import {getImageByAgent} from '../../config/agent';
import RidersList from '../../components/RidersList.jsx';
import {Breakpoints} from 'wobi-web-common';
import {useIntl} from 'react-intl';

const LoaderTravelImage = getImageByAgent('loader-travel.gif');

const OffersList = React.memo(
  ({
    className,
    offers,
    loaderState,
    keepChecking,
    submitAct,
    isSubmitting,
    handleErrorClose,
    isErrorOpen,
    handleNoOffers,
    formikSetField,
    formikErrors,
    formikValues,
    handleUpdateRiders,
    onRidersChange,
    hasPendingRiderChanges,
    validateForm,
  }) => {
    const { classes } = useStyles();
    const [showContactUsThanks, setShowContactUsThanks] = React.useState(false);
    const [isContactUsLoading, setContactUsLoading] = React.useState(false);
    const isDesktop = !useMediaQuery(`(max-width:${Breakpoints.mobile}px)`);
    const intl = useIntl();

    const submitContactUs = () => {
      setContactUsLoading(true);
      handleContactUsSubmit({
        thereNoOffers: true,
      }).then(() => {
        setShowContactUsThanks(true);
        setContactUsLoading(false);
      });
    };

    const offersContainerProps = {offers,
      submitAct,
      isSubmitting,
      hasPendingRiderChanges};
    const noOffersProps = {
      classes,
      showContactUsThanks,
      isContactUsLoading,
      submitContactUs,
      handleNoOffers,
    };

    return (
      <div className={className} data-testid='offers-main-container'>
        <Backdrop
          className={classes.backdrop}
          open={loaderState}
          data-testid='offers-loader'
        >
          <Paper style={{padding: 30}}>
            <Loader image={LoaderTravelImage}>
              <span>
                {intl.formatMessage({id: 'offers.loader.title'})}
              </span>
              <br />
              <span style={{fontSize: 20}}>
                {intl.formatMessage({id: 'offers.loader.description'})}
              </span>
            </Loader>
          </Paper>
        </Backdrop>
        <Grid container spacing={0} justifyContent='center'>
          {isDesktop ? (
            <Grid
              size={{
                xs: 3,
                lg: 3
              }}>
              <Paper className={classes.paperOffers} elevation={0}>
                <div className={classes.ridersListContainer}>
                  <RidersList
                    showTitleExplainWhatTodoText
                    formikSetField={formikSetField}
                    formikErrors={formikErrors}
                    formikValues={formikValues}
                    isShowSubDes={false}
                    validateForm={validateForm}
                    setFieldError={formikSetField}
                    onRidersChange={onRidersChange}
                    handleUpdateRiders={async () => {
                      const errors = await validateForm();
                      if (Object.keys(errors).length === 0) {
                        handleUpdateRiders();
                      }
                    }}
                  />
                </div>
              </Paper>
            </Grid>
          ) : null}
          <Grid
            className={classes.suggestionsTitle}
            size={{
              xs: 12,
              lg: 9,
              sm: 9
            }}>
            <Paper className={classes.paperOffers} elevation={0}>
              {offers.length ? (
                <OffersContainer {...offersContainerProps} />
              ) : !keepChecking && !offers.length ? (
                <NoOffers {...noOffersProps} />
              ) :
                false
              }
              <ErrorMessagePopup
                onClose={handleErrorClose}
                isOpen={isErrorOpen}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>
    );
  },
);

const useStyles = makeStyles()((theme) => ({
  root: {},
  ridersListContainer: {
    boxShadow: `8px 0 16px 0 ${Blue[150]}, 0 8px 16px 0 ${Blue[150]}, 0 -8px 16px 0 ${Blue[150]}`,
    backgroundColor: '#fff',
    borderRadius: 8,
  },
  suggestionsTitle: {
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(24.5),
    fontWeight: 450,
  },
  loaderContainer: {
    maxHeight: 300,
    transition: 'max-height 0.75s ease-in',
    overflow: 'hidden',
    margin: '88px 0 38px',

    '&.hide': {
      transition: 'max-height 0.75s ease-out',
      maxHeight: 0,
      margin: 0,
    },
  },
  progressLine: {
    width: 247,
    height: 3,
    margin: '10px 0 0',
  },
  paperOffers: {
    borderRadius: 8,
    backgroundColor:
      theme.palette.mode === 'dark' ?
        theme.palette.background.default :
        Blue[150],
    margin: '29px 0 0',
  },
  backdrop: {
    zIndex: 9,
  },
  noOffers: {
    padding: 10,

    '& .text': {
      fontSize: theme.typography.pxToRem(21),
      color: Black[170],
    },

    '& .contact_us_loader': {
      margin: '30px 70px',
    },

    '& button': {
      marginTop: 30,
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: 700,
    },

    '& .img-container': {
      marginTop: 50,
      '& img': {
        width: '100%',
        maxWidth: 478,
      },
    },
  },
  contactUsThanks: {
    display: 'inline-flex',
    alignItems: 'center',
    margin: '20px 0',
    padding: '15px 40px',
    borderRadius: 10,
    border: `1px solid ${Black[1]}`,

    '& img': {
      width: 80,
    },

    '& > div': {
      color: Black[170],
      marginLeft: 20,
      fontSize: theme.typography.pxToRem(15.75),
    },
  },
}));

OffersList.propTypes = {
  offers: PropTypes.object,
  submitAct: PropTypes.func,
  coversTitles: PropTypes.object,
  className: PropTypes.object,
  loaderState: PropTypes.bool,
  keepChecking: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  handleErrorClose: PropTypes.func,
  isErrorOpen: PropTypes.bool,
  formikErrors: PropTypes.object,
  handleNoOffers: PropTypes.func,
  formikSetField: PropTypes.func,
  formikValues: PropTypes.object,
  handleUpdateRiders: PropTypes.func,
  onRidersChange: PropTypes.func,
  hasPendingRiderChanges: PropTypes.bool,
  validateForm: PropTypes.func,
};

export default OffersList;
